import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import useSiteContent from "../lib/useSiteContent";
import { trackEvent } from "./GAScript";

/**
 * Founding Patron CTA band — captures only name + email. Backend writes to
 * `waitlist_signups` + outbox row (Resend dispatch lights up the day
 * RESEND_API_KEY is provisioned).
 *
 * Every visible string here is admin-editable via Site Content (cta_* keys).
 * Falls back to the strings shown below when a key is unset.
 *
 * The component pulls `/api/waitlist/stats` to show the live `247 / 500`
 * counter (or admin-marketing-override) — creates social-proof + scarcity.
 */
export default function FoundingPatronCTA() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [stats, setStats] = useState(null);
  const [refCode, setRefCode] = useState(null);
  const c = useSiteContent();
  // T() resolves a CMS-overridable label with fallback. Treats empty-string
  // overrides as "hide" (caller decides) but for required text we fall back.
  const T = (k, fallback) => {
    const v = c[k];
    return v !== undefined && v !== null && String(v).trim() !== "" ? v : fallback;
  };

  useEffect(() => {
    try {
      const urlRef = new URL(window.location.href).searchParams.get("ref");
      if (urlRef) {
        localStorage.setItem("oodel_ref_code", urlRef);
        setRefCode(urlRef);
      } else {
        setRefCode(localStorage.getItem("oodel_ref_code"));
      }
    } catch { /* SSR-safety no-op */ }
    api.get("/waitlist/stats")
      .then((r) => setStats(r.data || { cap: 0, confirmed: 0, total: 0 }))
      .catch(() => setStats({ cap: 0, confirmed: 0, total: 0 }));
  }, []);

  // Admin can close the waitlist entirely — render nothing in that case.
  const waitlistOpen = (c.waitlist_open || "true").toLowerCase() !== "false";
  if (!waitlistOpen) return null;

  const full = stats !== null && stats.confirmed >= stats.cap;
  const N = (val) => stats === null
    ? <span className="inline-block align-middle w-12 h-6 bg-neutral-200/80 rounded animate-pulse" aria-hidden />
    : <>{val}</>;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const url = new URL(window.location.href);
      const r = await api.post("/waitlist/join", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        utm_source: url.searchParams.get("utm_source"),
        utm_campaign: url.searchParams.get("utm_campaign"),
        utm_medium: url.searchParams.get("utm_medium"),
        referral_code: refCode || null,
      });
      if (r.data.already_patron) {
        toast.info("You're already a patron — please sign in.");
        setSuccess({ kind: "exists", message: r.data.message });
      } else if (r.data.already_on_list) {
        toast.success("You're already on the list — keep an eye on your inbox.");
        setSuccess({ kind: "exists", message: r.data.message });
      } else {
        toast.success("You're in — watch your inbox.");
        setSuccess({ kind: "new" });
        trackEvent("waitlist_join", { has_referral: !!refCode });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't join the list — try again?");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="border-y-2 border-black bg-gradient-to-br from-mango/10 via-white to-mango/5 py-20 px-6 themable-public" data-testid="founding-patron-cta">
      <div className="max-w-[1100px] mx-auto text-center">
        <div className="overline text-mango mb-4">
          {full
            ? T("cta_overline_full", "✦ JOIN THE WAITLIST ✦")
            : T("cta_overline_default", "✦ FOUNDING PATRON ✦")}
        </div>
        <h2 className="text-4xl md:text-6xl font-black tracking-tighter leading-tight mb-4" data-testid="cta-heading">
          {full ? (
            <>
              {T("cta_heading_full_lead", "The first")}{" "}
              <span className="text-mango">{N(stats?.cap)}</span>{" "}
              {T("cta_heading_full_trail", "seats are taken. Join the next wave.")}
            </>
          ) : (
            <>
              {T("cta_heading_lead", "Be one of the first")}{" "}
              <span className="text-mango">{N(stats?.cap)}</span>{" "}
              {T("cta_heading_trail", "voices defining how Pakistan thinks.")}
            </>
          )}
        </h2>
        <p className="text-lg md:text-xl text-neutral-700 mb-10 max-w-2xl mx-auto">
          {full
            ? T("cta_subtitle_full", "All Founding Patron spots are claimed. Join the regular waitlist — you'll still get early access and earn rewards.")
            : T("cta_subtitle_default", "Join the waitlist today — when our first wave launches, you'll be the first to take it, earn rewards, and get a Founding Patron badge for life.")}
        </p>

        {success ? (
          <div className="max-w-md mx-auto bg-white border-2 border-black p-6 hard-shadow" data-testid="cta-success">
            <div className="text-2xl mb-2">🎉</div>
            <div className="text-xl font-bold">
              {success.kind === "new"
                ? T("cta_success_new_title", "You're on the list!")
                : T("cta_success_existing_title", "Welcome back")}
            </div>
            <p className="text-sm text-neutral-600 mt-2">
              {success.kind === "new"
                ? T("cta_success_new_body", "We've sent a thank-you note to your inbox. There's nothing else to do right now — we'll email you the moment surveys go live.")
                : success.message || T("cta_success_existing_body", "Watch your inbox — we'll email you the moment surveys go live.")}
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="max-w-2xl mx-auto" data-testid="cta-form">
            {refCode && (
              <div className="mb-4 inline-block bg-mango/10 border-l-4 border-mango px-4 py-2 text-sm text-left" data-testid="cta-ref-badge">
                {T("cta_ref_badge_prefix", "✦ You were referred by a friend — code")}{" "}
                <span className="font-mono font-bold">{refCode}</span>
                {T("cta_ref_badge_suffix", ". They'll earn rewards once you take your first survey.")}
              </div>
            )}
            <div className="flex flex-col md:flex-row gap-3">
              <input
                type="text"
                placeholder={T("cta_name_placeholder", "Your name")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="flex-1 px-4 py-4 border-2 border-black bg-white text-black placeholder-neutral-500 text-base focus:outline-none focus:ring-2 focus:ring-mango"
                data-testid="cta-name"
                required
              />
              <input
                type="email"
                placeholder={T("cta_email_placeholder", "email@example.com")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-[1.4] px-4 py-4 border-2 border-black bg-white text-black placeholder-neutral-500 text-base focus:outline-none focus:ring-2 focus:ring-mango"
                data-testid="cta-email"
                required
              />
              <button
                type="submit"
                disabled={submitting}
                className="px-8 py-4 bg-mango text-white font-bold border-2 border-black hover:bg-black hover:text-white transition-colors disabled:opacity-50 hard-shadow"
                data-testid="cta-submit"
              >
                {submitting
                  ? T("cta_submit_loading_label", "Joining…")
                  : T("cta_submit_label", "Join →")}
              </button>
            </div>
          </form>
        )}

        <div className="mt-8 flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-sm text-neutral-600">
          <span data-testid="cta-counter">
            ✦ <strong className="font-mono">{N(stats?.confirmed)}</strong> / <strong className="font-mono">{N(stats?.cap)}</strong>{" "}
            {T("cta_counter_suffix", "joined")}
          </span>
          <span>{T("cta_perk_1", "✦ No spam, ever")}</span>
          <span>{T("cta_perk_2", "✦ Free forever")}</span>
        </div>
      </div>
    </section>
  );
}
